'use client'

import { useEffect, useState } from 'react'
import { Col, Row } from 'react-bootstrap'

import tick from '@/assets/images/microsite/benefits/tick.svg'
import { useIsMobile } from '@/hooks/useIsMobile'
import { parseCmsHtml } from '@/utils'

interface FeaturesProps {
  heading?: string
  benefitsData: {
    title: string
    content: string
    subtext?: string
  }[]
  desktopImages?: string
}

const Features = ({
  heading = 'Features & Benefits',
  benefitsData = [],
  desktopImages
}: FeaturesProps) => {
  const [activeItems, setActiveItems] = useState<boolean[]>(
    Array.from({ length: benefitsData.length }, () => false)
  )

  const [positions, setPositions] = useState<number[]>([])
  const isMobile = useIsMobile({ maxWidth: 767 })

  useEffect(() => {
    setActiveItems(Array.from({ length: benefitsData.length }, () => false))
  }, [benefitsData])

  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll('.benefit-items')
      const updated = [...activeItems]

      elements.forEach((el, i) => {
        const rect = el.getBoundingClientRect()
        updated[i] = rect.top <= window.innerHeight * 0.75
      })

      setActiveItems(updated)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const calcPositions = () => {
      const els = document.querySelectorAll('.benefit-items')
      const pos: number[] = []

      els.forEach(el => {
        pos.push((el as HTMLElement).offsetTop)
      })

      setPositions(pos)
    }

    const timer = setTimeout(calcPositions, 100)
    window.addEventListener('resize', calcPositions)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', calcPositions)
    }
  }, [benefitsData])

  const progress = (activeItems.filter(Boolean).length / (activeItems.length || 1)) * 100

  return (
    <section
      className="px-3 mx-sm-3 py-5"
      style={{
        background: 'linear-gradient(180deg, #FFF 0%, #F0F7FD 100%)'
      }}
    >
      <div className="container pt-md-5">
        <h2 className="text-center heading-h2 mb-md-5 mb-2 fw-bold pt-md-5">
          {parseCmsHtml(heading)}
        </h2>

        <Row className="align-items-start">
          <Col lg={5} xs={12} className="mb-4 mb-lg-0">
            <img src={desktopImages} alt="Features" className="img-fluid w-100" />
          </Col>

          <Col lg={7} xs={12}>
            <div
              className="position-relative d-flex flex-column"
              style={{ paddingLeft: isMobile ? '50px' : '100px' }}
            >
              <div
                className="position-absolute top-0 h-100"
                style={{
                  left: isMobile ? '0px' : '20px',
                  width: isMobile ? '30px' : '36px',
                  background: '#E5E5E5',
                  borderRadius: '100px',
                  overflow: 'hidden'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    height: `${progress}%`,
                    width: '100%',
                    background: 'linear-gradient(180deg, #140F95 0%, #221AFB 100%)',
                    borderRadius: '100px',
                    transition: 'height 0.6s ease-out',
                    willChange: 'height'
                  }}
                />

                {benefitsData.map((item, index) => (
                  <div
                    key={item.title}
                    style={{
                      position: 'absolute',
                      top: positions[index] || '10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 2,
                      opacity: activeItems[index] ? 1 : 0.4,
                      transition: 'opacity 0.3s ease'
                    }}
                  >
                    <div
                      style={{
                        width: '26px',
                        height: '26px',
                        background: '#fff',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                      }}
                    >
                      <img src={tick.src} alt="tick" className="w-100" />
                    </div>
                  </div>
                ))}
              </div>

              {benefitsData.map(item => (
                <div className="benefit-items position-relative mb-5" key={item.title}>
                  <h3 className="fw-bold fs-md-20 mb-2 primary-color-dark fs-18">
                    {parseCmsHtml(item.title)}
                  </h3>

                  <p className="gray-500 fs-md-16 fs-14 pe-md-5">
                    {typeof item.content === 'string' ? parseCmsHtml(item.content) : item.content}
                  </p>

                  {item.subtext && <p className="mt-2 text-secondary small">{item.subtext}</p>}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </div>
    </section>
  )
}

export default Features
